import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, rx } from '@app/shared/rx';
import { EHeaderName, II18nConfig } from '@app/shared/types';
import {
  concat,
  flow,
  get,
  gte,
  includes,
  isEmpty,
  isNil,
  lt,
  some,
  split,
  toLower,
} from 'lodash';
import {
  REQUEST_CALLER_IDENTIFY,
  REQUEST_SPECIFIC_I18N_CONFIG,
} from './user-defined-i18n.constants';
import {
  getAddedI18nNestedPaths,
  getI18nConfigFromUserDefinedConfig,
  getUserDefineStringPaths,
} from '@i18n/user-defined-strings.helper';
import { isXenialUrl } from '@app/shared/utils/common-utils';
import { I18nService } from '@app/shared/services/i18n/i18n.service';
import { TranslationService } from '@app/shared/services/i18n';
import {
  HTTP_SUCCESS_STATUS_LOWER_BOUND,
  HTTP_SUCCESS_STATUS_UPPER_BOUND,
} from '@config/global-constants';

/**
 * These routes are used to skip user defined locales processing
 */
const ROUTES_TO_SKIP_USER_DEFINED_LOCALES = [
  'dictionary', // dictionary data
  'token/verify', // token verify
  'boh', // boh related APIs
];

/**
 * Notice: this interceptor works only with absolute URLs.
 *         It means that {@link UrlInterceptor} should be provided before.
 * You need make sure this interceptor is provided at the end of the
 * interceptor chain, so that it can capture the caller info set by
 * XVIHttpClient response.
 */
@Injectable()
export class UserDefinedI18nInterceptor implements HttpInterceptor {
  public constructor(
    private i18nService: I18nService,
    public translationService: TranslationService,
  ) {}

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const callerIdentify = request.context.get(REQUEST_CALLER_IDENTIFY);

    const contextSpecialI18nConfig = request.context.get(
      REQUEST_SPECIFIC_I18N_CONFIG,
    );

    const specificI18nConfig = getI18nConfigFromUserDefinedConfig(
      callerIdentify,
      contextSpecialI18nConfig,
    );

    const skipTranslations =
      !isXenialUrl(request.url) ||
      !this.i18nService.isNeedUserDefinedTranslate() ||
      some(ROUTES_TO_SKIP_USER_DEFINED_LOCALES, (route) =>
        includes(request.url, route),
      );

    if (skipTranslations) {
      const newRequestWhenSkip = flow((req) =>
        this.modifyRequestBody(req, {
          callerIdentify,
          specificI18nConfig,
          skipTranslations,
        }),
      )(request);

      return next.handle(newRequestWhenSkip);
    }

    const { userLocale } = this.translationService.getLocals();

    const newRequest = flow(
      (req) => this.modifyHeaders(req, { userLocale }),
      (req) =>
        this.modifyRequestProjects(req, {
          callerIdentify,
          specificI18nConfig,
        }),
      (req) =>
        this.modifyRequestBody(req, {
          callerIdentify,
          specificI18nConfig,
        }),
    )(request);

    return next.handle(newRequest).pipe(
      rx.map((event: HttpEvent<any>) => {
        const needTranslate =
          !isNil(event) &&
          event instanceof HttpResponse &&
          gte(event.status, HTTP_SUCCESS_STATUS_LOWER_BOUND) &&
          lt(event.status, HTTP_SUCCESS_STATUS_UPPER_BOUND) &&
          !isEmpty(event.body) &&
          !isEmpty(getUserDefineStringPaths(callerIdentify));

        if (!needTranslate || !callerIdentify) {
          return event;
        }

        const responseBody = (event as HttpResponse<any>)?.body;

        const newBody = this.i18nService.applyUserDefinedTranslations(
          callerIdentify,
          responseBody,
          specificI18nConfig,
        );

        return (event as HttpResponse<any>).clone({ body: newBody });
      }),
    );
  }

  protected modifyHeaders(
    request: HttpRequest<any>,
    options: {
      userLocale: string;
    },
  ): HttpRequest<any> {
    const { userLocale } = options ?? {};

    if (!isEmpty(userLocale) && !request.headers.has(EHeaderName.XLocales)) {
      return request.clone({
        headers: request.headers.set(EHeaderName.XLocales, userLocale),
      });
    }

    return request;
  }

  protected modifyRequestProjects(
    request: HttpRequest<any>,
    options: {
      callerIdentify: string;
      specificI18nConfig?: II18nConfig;
      userLocale?: string;
    },
  ): HttpRequest<any> {
    const { callerIdentify, userLocale } = options;
    const { params } = request;

    const nestedProjectPaths = split(params.get('$select') as string, ',');
    const addedI18nProjects = getAddedI18nNestedPaths(
      callerIdentify,
      nestedProjectPaths,
      {
        specialLocale: userLocale,
      },
    );

    if (isEmpty(addedI18nProjects)) {
      return request;
    }

    const newProjects = concat(nestedProjectPaths, addedI18nProjects);

    return request.clone({
      params: params.set('$select', newProjects.toString()),
    });
  }

  protected modifyRequestBody(
    request: HttpRequest<any>,
    options: {
      callerIdentify: string;
      specificI18nConfig?: II18nConfig;
      skipTranslations?: boolean;
    },
  ): HttpRequest<any> {
    /**
     * will return directly if:
     * 1. not PUT/POST/PATCH method
     * 2. body is empty
     */
    if (
      !includes(['put', 'post', 'patch'], toLower(request.method)) ||
      isEmpty(request.body)
    ) {
      return request;
    }

    return flow(
      (req) =>
        // only revert when not skip translations
        !options.skipTranslations
          ? this.revertUserDefinedTranslationsInBody(req, options)
          : req,
      (req) => this.removeI18nFieldInBody(req, options),
    )(request) as HttpRequest<any>;
  }

  protected revertUserDefinedTranslationsInBody(
    request: HttpRequest<any>,
    options: { callerIdentify: string; specificI18nConfig?: II18nConfig },
  ): HttpRequest<any> {
    const { callerIdentify, specificI18nConfig } = options;

    if (!this.isRevertEnabled(specificI18nConfig)) {
      return request;
    }

    return request.clone({
      body: this.i18nService.revertUserDefinedTranslations(
        request.body,
        callerIdentify,
        specificI18nConfig,
      ),
    });
  }

  protected removeI18nFieldInBody(
    request: HttpRequest<any>,
    options: { callerIdentify: string; specificI18nConfig?: II18nConfig },
  ): HttpRequest<any> {
    const { specificI18nConfig } = options;

    if (this.isKeepI18nFields(specificI18nConfig)) {
      return request;
    }

    return request.clone({
      body: this.i18nService.removeI18nField(request.body),
    });
  }

  protected isKeepI18nFields(specificI18nConfig?: II18nConfig): boolean {
    return get(specificI18nConfig, 'keepI18nFields', false);
  }

  /**
   * will decide whether to enable revert user defined translations when save data
   *
   * @param specificI18nConfig
   * @returns
   */
  protected isRevertEnabled(specificI18nConfig?: II18nConfig): boolean {
    const enableRevert = get(specificI18nConfig, 'enableRevert');

    return isNil(enableRevert)
      ? !this.isKeepI18nFields(specificI18nConfig)
      : enableRevert;
  }
}
